import { useState, useEffect } from "react";
import { 
  Users, 
  FileText, 
  Trophy, 
  TrendingUp, 
  PoundSterling,
  BarChart3,
  Target,
  XCircle
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MetricCard = ({ icon: Icon, label, value, subValue, color, onClick }) => (
  <button
    onClick={onClick}
    className={`bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md hover:border-${color}-200 transition-all text-left w-full group`}
    data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
        {subValue && <p className="text-[10px] text-gray-400 mt-0.5">{subValue}</p>}
      </div>
    </div>
  </button>
);

const CRMMetrics = ({ token }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API}/owner/crm/metrics`, { headers });
      setMetrics(response.data);
    } catch (error) {
      console.error("Error fetching CRM metrics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 animate-pulse">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-gray-100 rounded-xl h-24"></div>
        ))}
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="space-y-4" data-testid="crm-metrics">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard
          icon={Users}
          label="Leads This Month"
          value={metrics.total_leads_this_month || 0}
          color="blue"
        />
        <MetricCard
          icon={FileText}
          label="Quotes Sent"
          value={metrics.quotes_sent || 0}
          color="indigo"
        />
        <MetricCard
          icon={Trophy}
          label="Jobs Won"
          value={metrics.jobs_won || 0}
          color="green"
        />
        <MetricCard
          icon={XCircle}
          label="Jobs Lost"
          value={metrics.jobs_lost || 0}
          color="gray"
        />
        <MetricCard
          icon={Target}
          label="Conversion Rate"
          value={`${metrics.conversion_rate || 0}%`}
          subValue={`${metrics.jobs_won || 0}/${metrics.quotes_sent || 0} quotes`}
          color="purple"
        />
        <MetricCard
          icon={PoundSterling}
          label="Pipeline Value"
          value={`£${(metrics.estimated_pipeline_value || 0).toLocaleString()}`}
          subValue="Active opportunities"
          color="emerald"
        />
      </div>
      
      {/* Revenue highlight */}
      {metrics.revenue_this_month > 0 && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Revenue This Month</p>
              <p className="text-3xl font-bold">£{metrics.revenue_this_month.toLocaleString()}</p>
            </div>
            <TrendingUp className="w-12 h-12 opacity-50" />
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMMetrics;
